import React, { useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { QueryableFieldsQuery } from '../../../../types/generated-types';
import {
  ITieredEntitySelectionCriterion,
  EntityComparatorType,
  EntityComparator,
  EntityValueType,
} from '../../../../common/types/shared/entitySelection';
import { SelectionPathBuilder } from './SelectionPathBuilder';
import { getCriterionInputConfig } from '../helpers';
import { getPhysicalEnumValuesForType } from '../helpers';

interface CriterionBuilderProps {
  fieldsData: QueryableFieldsQuery['queryableFields'];
  targetModel: string;
  selectionSpec: ITieredEntitySelectionCriterion;
  onChange: (criterion: ITieredEntitySelectionCriterion) => void;
  tier: number;
  simplified?: boolean;
}

export const CriterionBuilder: React.FC<CriterionBuilderProps> = ({
  fieldsData,
  targetModel,
  selectionSpec: criterion,
  onChange,
  tier = 0,
  simplified = false,
}) => {
  const getFieldType = useCallback(
    (
      fieldsData: QueryableFieldsQuery['queryableFields'],
      path: string,
    ): string | null => {
      if (!fieldsData) return null;
      const pathParts = path.split('.');
      let currentFields = fieldsData;
      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        if (!currentFields[part]) return null;
        if (i === pathParts.length - 1) {
          const value = currentFields[part];
          if (Array.isArray(value)) {
            if (
              value.length === 1 &&
              Array.isArray(value[0]) &&
              value[0].every((v) => typeof v === 'string')
            ) {
              return 'ArrayOfEnum';
            }
            // Check if it's an array of a specific type
            if (value.length === 1 && typeof value[0] === 'string') {
              return `[${value[0]}]`;
            } else {
              return 'PhysicalEnum';
            }
            // return 'String';
          }
          return value;
        }
        currentFields = currentFields[part];
      }
      return null;
    },
    [],
  );

  const isEnumType = (type: string | null): boolean => {
    if (!type) return false;
    return (
      type === 'PhysicalEnum' ||
      type.startsWith('Enum') ||
      type.startsWith('[Enum')
    );
  };

  const getEnumValues = (): string[] => {
    return getEnumValuesForPath(criterion.selectionPath) ?? [];
  };

  // Helper to get enum values array for a path (for physical array fields)
  const getEnumValuesForPath = (path: string): string[] | null => {
    if (!fieldsData) return null;
    const pathParts = path.split('.');
    let currentFields = fieldsData;
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      if (!currentFields[part]) return null;
      if (i === pathParts.length - 1) {
        const value = currentFields[part];
        if (Array.isArray(value)) return value;
        return null;
      }
      currentFields = currentFields[part];
    }
    return null;
  };

  // Helper to get array-of-enum values for a path
  const getArrayOfEnumValues = (path: string): string[] => {
    if (!fieldsData) return [];
    const pathParts = path.split('.');
    let currentFields = fieldsData;
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      if (!currentFields[part]) return [];
      if (i === pathParts.length - 1) {
        const value = currentFields[part];
        if (
          Array.isArray(value) &&
          value.length === 1 &&
          Array.isArray(value[0]) &&
          value[0].every((v) => typeof v === 'string')
        ) {
          return value[0];
        }
        return [];
      }
      currentFields = currentFields[part];
    }
    return [];
  };

  const getApplicableComparators = useCallback(
    (fieldType: string | null): EntityComparatorType[] => {
      if (!fieldType) return [];

      if (simplified) {
        return [EntityComparator.EQUAL_TO];
      }

      if (fieldType === 'ArrayOfEnum') {
        return [
          EntityComparator.ARRAY_CONTAINS,
          EntityComparator.ARRAY_NOT_CONTAINS,
          EntityComparator.ARRAY_CONTAINS_ALL,
          EntityComparator.ARRAY_SIZE,
          EntityComparator.ARRAY_SIZE_GREATER_THAN,
          EntityComparator.ARRAY_SIZE_LESS_THAN,
          EntityComparator.ALL,
          EntityComparator.NONE,
          EntityComparator.SOME,
          EntityComparator.NOT_SOME,
          EntityComparator.EMPTY,
          EntityComparator.NOT_EMPTY,
          EntityComparator.EXISTS,
          EntityComparator.NOT_EXISTS,
        ];
      }

      // Array type comparators
      if (fieldType.startsWith('[')) {
        const baseType = fieldType.slice(1, -1); // Remove the [ and ]
        if (isEnumType(baseType)) {
          return [
            EntityComparator.ARRAY_CONTAINS,
            EntityComparator.ARRAY_NOT_CONTAINS,
            EntityComparator.ARRAY_CONTAINS_ALL,
            EntityComparator.ARRAY_SIZE,
            EntityComparator.ARRAY_SIZE_GREATER_THAN,
            EntityComparator.ARRAY_SIZE_LESS_THAN,
            EntityComparator.ALL,
            EntityComparator.NONE,
            EntityComparator.SOME,
            EntityComparator.NOT_SOME,
            EntityComparator.EMPTY,
            EntityComparator.NOT_EMPTY,
            EntityComparator.EXISTS,
            EntityComparator.NOT_EXISTS,
          ];
        }
        return [
          EntityComparator.ARRAY_CONTAINS,
          EntityComparator.ARRAY_NOT_CONTAINS,
          EntityComparator.ARRAY_CONTAINS_ALL,
          EntityComparator.ARRAY_SIZE,
          EntityComparator.ARRAY_SIZE_GREATER_THAN,
          EntityComparator.ARRAY_SIZE_LESS_THAN,
          EntityComparator.ALL,
          EntityComparator.NONE,
          EntityComparator.SOME,
          EntityComparator.NOT_SOME,
          EntityComparator.EMPTY,
          EntityComparator.NOT_EMPTY,
          EntityComparator.EXISTS,
          EntityComparator.NOT_EXISTS,
        ];
      }

      // String type comparators
      if (fieldType === 'String') {
        return [
          EntityComparator.EQUAL_TO,
          EntityComparator.NOT_EQUAL_TO,
          EntityComparator.CONTAINS,
          EntityComparator.NOT_CONTAINS,
          EntityComparator.STARTS_WITH,
          EntityComparator.ENDS_WITH,
          EntityComparator.TEXT_CONTAINS,
          EntityComparator.TEXT_STARTS_WITH,
          EntityComparator.TEXT_ENDS_WITH,
          EntityComparator.TEXT_MATCHES,
          EntityComparator.IS_NULL,
          EntityComparator.IS_NOT_NULL,
          EntityComparator.REGEX,
          EntityComparator.NOT_REGEX,
          EntityComparator.EMPTY,
          EntityComparator.NOT_EMPTY,
          EntityComparator.EXISTS,
          EntityComparator.NOT_EXISTS,
        ];
      }

      // Numeric type comparators (Float, Int)
      if (fieldType === 'Float' || fieldType === 'Int') {
        return [
          EntityComparator.EQUAL_TO,
          EntityComparator.NOT_EQUAL_TO,
          EntityComparator.GREATER_THAN,
          EntityComparator.LESS_THAN,
          EntityComparator.GREATER_THAN_OR_EQUAL_TO,
          EntityComparator.LESS_THAN_OR_EQUAL_TO,
          EntityComparator.BETWEEN,
          EntityComparator.NOT_BETWEEN,
          EntityComparator.IN_RANGE,
          EntityComparator.NOT_IN_RANGE,
          EntityComparator.IS_NULL,
          EntityComparator.IS_NOT_NULL,
          EntityComparator.EXISTS,
          EntityComparator.NOT_EXISTS,
        ];
      }

      // Boolean type comparators
      if (fieldType === 'Boolean') {
        return [
          EntityComparator.EQUAL_TO,
          EntityComparator.NOT_EQUAL_TO,
          EntityComparator.IS_NULL,
          EntityComparator.IS_NOT_NULL,
          EntityComparator.EXISTS,
          EntityComparator.NOT_EXISTS,
        ];
      }

      // Enum type comparators
      if (isEnumType(fieldType)) {
        return [
          EntityComparator.EQUAL_TO,
          EntityComparator.NOT_EQUAL_TO,
          EntityComparator.IN_COLLECTION,
          EntityComparator.NOT_IN_COLLECTION,
          EntityComparator.IS_NULL,
          EntityComparator.IS_NOT_NULL,
          EntityComparator.EXISTS,
          EntityComparator.NOT_EXISTS,
        ];
      }

      // Default comparators for other types
      return [
        EntityComparator.EQUAL_TO,
        EntityComparator.NOT_EQUAL_TO,
        EntityComparator.IS_NULL,
        EntityComparator.IS_NOT_NULL,
        EntityComparator.EXISTS,
        EntityComparator.NOT_EXISTS,
      ];
    },
    [simplified],
  );

  // Add this effect after the useEffect for searchInputRef
  useEffect(() => {
    // Set default comparator and valueType when field type is determined
    const fieldType = getFieldType(fieldsData, criterion.selectionPath);
    if (fieldType && !criterion.comparator) {
      // Only set if comparator is not already set
      let valueType: EntityValueType;
      // Map field type to EntityValueType
      switch (fieldType) {
        case 'Float':
        case 'Int':
          valueType = EntityValueType.NUMBER;
          break;
        case 'Boolean':
          valueType = EntityValueType.BOOLEAN;
          break;
        case 'Date':
          valueType = EntityValueType.DATE;
          break;
        case 'DateTime':
          valueType = EntityValueType.DATETIME;
          break;
        case 'Percentage':
          valueType = EntityValueType.PERCENTAGE;
          break;
        case 'Temperature':
          valueType = EntityValueType.TEMPERATURE;
          break;
        case 'Humidity':
          valueType = EntityValueType.HUMIDITY;
          break;
        case 'Pressure':
          valueType = EntityValueType.PRESSURE;
          break;
        case 'Voltage':
          valueType = EntityValueType.VOLTAGE;
          break;
        case 'Current':
          valueType = EntityValueType.CURRENT;
          break;
        case 'Power':
          valueType = EntityValueType.POWER;
          break;
        case 'Energy':
          valueType = EntityValueType.ENERGY;
          break;
        case 'FlowRate':
          valueType = EntityValueType.FLOW_RATE;
          break;
        case 'Concentration':
          valueType = EntityValueType.CONCENTRATION;
          break;
        case 'Level':
          valueType = EntityValueType.LEVEL;
          break;
        case 'Speed':
          valueType = EntityValueType.SPEED;
          break;
        case 'Frequency':
          valueType = EntityValueType.FREQUENCY;
          break;
        case 'Airflow':
          valueType = EntityValueType.AIRFLOW;
          break;
        case 'String':
          valueType = EntityValueType.STRING;
          break;
        default:
          valueType = EntityValueType.STRING;
      }

      const comparators = getApplicableComparators(fieldType);
      if (comparators.length > 0) {
        onChange({
          ...criterion,
          comparator: comparators[0],
          valueType,
        });
      }
    }
  }, [criterion, getApplicableComparators, getFieldType, onChange, fieldsData]);

  // Add this effect to update valueType when field type changes
  useEffect(() => {
    const fieldType = getFieldType(fieldsData, criterion.selectionPath);
    if (fieldType) {
      let valueType: EntityValueType;
      switch (fieldType) {
        case 'Float':
        case 'Int':
          valueType = EntityValueType.NUMBER;
          break;
        case 'Boolean':
          valueType = EntityValueType.BOOLEAN;
          break;
        case 'Date':
          valueType = EntityValueType.DATE;
          break;
        case 'DateTime':
          valueType = EntityValueType.DATETIME;
          break;
        case 'Percentage':
          valueType = EntityValueType.PERCENTAGE;
          break;
        case 'Temperature':
          valueType = EntityValueType.TEMPERATURE;
          break;
        case 'Humidity':
          valueType = EntityValueType.HUMIDITY;
          break;
        case 'Pressure':
          valueType = EntityValueType.PRESSURE;
          break;
        case 'Voltage':
          valueType = EntityValueType.VOLTAGE;
          break;
        case 'Current':
          valueType = EntityValueType.CURRENT;
          break;
        case 'Power':
          valueType = EntityValueType.POWER;
          break;
        case 'Energy':
          valueType = EntityValueType.ENERGY;
          break;
        case 'FlowRate':
          valueType = EntityValueType.FLOW_RATE;
          break;
        case 'Concentration':
          valueType = EntityValueType.CONCENTRATION;
          break;
        case 'Level':
          valueType = EntityValueType.LEVEL;
          break;
        case 'Speed':
          valueType = EntityValueType.SPEED;
          break;
        case 'Frequency':
          valueType = EntityValueType.FREQUENCY;
          break;
        case 'Airflow':
          valueType = EntityValueType.AIRFLOW;
          break;
        case 'String':
          valueType = EntityValueType.STRING;
          break;
        default:
          valueType = EntityValueType.STRING;
      }

      if (criterion.valueType !== valueType) {
        onChange({
          ...criterion,
          valueType,
        });
      }
    }
  }, [criterion, getFieldType, onChange, fieldsData]);

  // Memoize the value input handler to prevent unnecessary re-renders
  const handleValueChange = useCallback(
    (newValue: string | string[]) => {
      onChange({
        ...criterion,
        values: Array.isArray(newValue) ? newValue : [newValue],
      });
    },
    [criterion, onChange],
  );

  const getValueInput = () => {
    const fieldType = getFieldType(fieldsData, criterion.selectionPath);
    const inputConfig = getCriterionInputConfig(
      criterion.valueType as EntityValueType,
      criterion.comparator,
    );
    const criterionIndex = Math.max(
      Math.min((criterion.values ?? []).length, tier) - 1,
      0,
    );
    const criterionValue = (criterion.values ?? [undefined])[criterionIndex];

    // Handle existence and emptiness checks - these don't need value inputs
    if (
      criterion.comparator === EntityComparator.EXISTS ||
      criterion.comparator === EntityComparator.NOT_EXISTS ||
      criterion.comparator === EntityComparator.EMPTY ||
      criterion.comparator === EntityComparator.NOT_EMPTY
    ) {
      return null;
    }

    // Handle regex operators
    if (
      criterion.comparator === EntityComparator.REGEX ||
      criterion.comparator === EntityComparator.NOT_REGEX
    ) {
      return (
        <TextField
          fullWidth
          value={criterionValue || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder="Enter regular expression pattern"
          helperText="Use JavaScript regular expression syntax"
        />
      );
    }

    // Handle array quantifiers (ALL, NONE, SOME, NOT_SOME)
    if (
      criterion.comparator === EntityComparator.ALL ||
      criterion.comparator === EntityComparator.NONE ||
      criterion.comparator === EntityComparator.SOME ||
      criterion.comparator === EntityComparator.NOT_SOME
    ) {
      return (
        <TextField
          fullWidth
          value={criterionValue || ''}
          onChange={(e) => {
            const error = inputConfig.validate?.(e.target.value);
            if (!error) {
              handleValueChange(e.target.value);
            }
          }}
          error={!!inputConfig.validate?.(criterionValue || '')}
          helperText={inputConfig.helperText}
        />
      );
    }

    // If the selected field is an array of enum values, render a multi-select dropdown
    const arrayOfEnumValues = getArrayOfEnumValues(criterion.selectionPath);
    if (fieldType === 'ArrayOfEnum' && arrayOfEnumValues.length > 0) {
      return (
        <FormControl fullWidth>
          <InputLabel>Values</InputLabel>
          <Select
            multiple
            value={criterionValue || []}
            onChange={(e) => handleValueChange(e.target.value as string[])}
            renderValue={(selected) => (selected as string[]).join(', ')}
          >
            {arrayOfEnumValues.map((value: string) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // If the selected field is a physical array of enum values, render a dropdown
    const physicalEnumValues = getPhysicalEnumValuesForType(
      criterion.valueType as EntityValueType,
    );
    if (physicalEnumValues && physicalEnumValues.length > 0) {
      return (
        <FormControl fullWidth>
          <InputLabel>Value</InputLabel>
          <Select
            value={criterionValue || ''}
            onChange={(e) => handleValueChange(e.target.value as string)}
          >
            {physicalEnumValues.map((value: string) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Handle collection operators (IN_COLLECTION, NOT_IN_COLLECTION)
    if (
      criterion.comparator === EntityComparator.IN_COLLECTION ||
      criterion.comparator === EntityComparator.NOT_IN_COLLECTION
    ) {
      const isEnum = isEnumType(fieldType);
      if (isEnum) {
        const enumValues = getEnumValues();
        return (
          <FormControl fullWidth>
            <InputLabel>Values</InputLabel>
            <Select
              multiple
              value={criterionValue || []}
              onChange={(e) => handleValueChange(e.target.value as string[])}
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {enumValues.map((value: string) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            value={
              Array.isArray(criterionValue) ? criterionValue.join('\n') : ''
            }
            onChange={(e) => {
              const error = inputConfig.validate?.(e.target.value);
              if (!error) {
                handleValueChange(e.target.value.split('\n').filter(Boolean));
              }
            }}
            error={
              !!inputConfig.validate?.(
                Array.isArray(criterionValue) ? criterionValue.join('\n') : '',
              )
            }
            helperText={inputConfig.helperText}
          />
        );
      }
    }

    // Handle range operators (BETWEEN, NOT_BETWEEN)
    if (
      criterion.comparator === EntityComparator.BETWEEN ||
      criterion.comparator === EntityComparator.NOT_BETWEEN
    ) {
      return (
        <TargetRangeEditor
          fieldType={fieldType}
          criterion={criterion}
          onChange={onChange}
          inputConfig={inputConfig}
        />
      );
    }

    // Handle range format operators (IN_RANGE, NOT_IN_RANGE)
    if (
      criterion.comparator === EntityComparator.IN_RANGE ||
      criterion.comparator === EntityComparator.NOT_IN_RANGE
    ) {
      return (
        <TextField
          fullWidth
          value={criterionValue?.[0] || ''}
          onChange={(e) => {
            const error = inputConfig.validate?.(e.target.value);
            if (!error) {
              handleValueChange(e.target.value);
            }
          }}
          error={!!inputConfig.validate?.(criterionValue?.[0] || '')}
          helperText={inputConfig.helperText}
        />
      );
    }

    // Handle text search operators (CONTAINS, NOT_CONTAINS, STARTS_WITH, ENDS_WITH)
    if (
      criterion.comparator === EntityComparator.CONTAINS ||
      criterion.comparator === EntityComparator.NOT_CONTAINS ||
      criterion.comparator === EntityComparator.STARTS_WITH ||
      criterion.comparator === EntityComparator.ENDS_WITH
    ) {
      return (
        <TextField
          fullWidth
          value={criterionValue || ''}
          onChange={(e) => {
            const error = inputConfig.validate?.(e.target.value);
            if (!error) {
              handleValueChange(e.target.value);
            }
          }}
          error={!!inputConfig.validate?.(criterionValue || '')}
          helperText={inputConfig.helperText}
        />
      );
    }

    // Handle array size operators
    if (
      criterion.comparator === EntityComparator.ARRAY_SIZE ||
      criterion.comparator === EntityComparator.ARRAY_SIZE_GREATER_THAN ||
      criterion.comparator === EntityComparator.ARRAY_SIZE_LESS_THAN
    ) {
      return (
        <TextField
          type="number"
          fullWidth
          value={criterionValue || ''}
          onChange={(e) => {
            const error = inputConfig.validate?.(e.target.value);
            if (!error) {
              handleValueChange(e.target.value);
            }
          }}
          error={!!inputConfig.validate?.(criterionValue || '')}
          helperText={inputConfig.helperText}
        />
      );
    }

    // Handle enum values
    const isEnum = isEnumType(fieldType);
    if (isEnum) {
      const enumValues = getEnumValues();
      return (
        <FormControl fullWidth>
          <InputLabel>Value</InputLabel>
          <Select
            value={criterionValue || ''}
            onChange={(e) => handleValueChange(e.target.value as string)}
          >
            {enumValues.map((value: string) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Default case - use the input configuration
    return (
      <TextField
        fullWidth
        type={
          fieldType === 'Float' || fieldType === 'Int'
            ? 'number'
            : inputConfig.type
        }
        value={criterionValue || ''}
        onChange={(e) => {
          const error = inputConfig.validate?.(e.target.value);
          if (!error) {
            handleValueChange(e.target.value);
          }
        }}
        inputProps={{
          ...inputConfig.inputProps,
          type:
            fieldType === 'Float' || fieldType === 'Int'
              ? 'number'
              : inputConfig.type,
          step: fieldType === 'Float' ? 'any' : undefined,
        }}
        error={!!inputConfig.validate?.(criterionValue || '')}
        helperText={inputConfig.helperText}
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SelectionPathBuilder
        value={criterion.selectionPath}
        onChange={(value) => onChange({ ...criterion, selectionPath: value })}
        fieldsData={fieldsData}
        targetModel={targetModel}
      />
      {criterion.selectionPath && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'start' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Comparator</InputLabel>
            <Select
              value={criterion.comparator || ''}
              onChange={(e) => {
                onChange({
                  ...criterion,
                  comparator: e.target.value as EntityComparatorType,
                });
              }}
            >
              {getApplicableComparators(
                getFieldType(fieldsData, criterion.selectionPath),
              ).map((comp) => (
                <MenuItem key={comp} value={comp}>
                  {comp}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div style={{ width: '100%' }}>{getValueInput()}</div>
        </Box>
      )}
    </Box>
  );
};

interface TargetRangeEditorProps {
  fieldType: string | null;
  criterion: ITieredEntitySelectionCriterion;
  onChange: (criterion: ITieredEntitySelectionCriterion) => void;
  inputConfig: {
    validate?: (value: string) => string | null;
    helperText: string;
  };
}

const TargetRangeEditor: React.FC<TargetRangeEditorProps> = ({
  fieldType,
  criterion,
  onChange,
  inputConfig,
}) => {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <TextField
        label="Min"
        type={fieldType === 'Float' || fieldType === 'Int' ? 'number' : 'text'}
        value={criterion.values?.[0] || ''}
        onChange={(e) => {
          const error = inputConfig.validate?.(e.target.value);
          if (!error) {
            const newValues = [...(criterion.values || [])];
            newValues[0] = e.target.value;
            onChange({
              ...criterion,
              values: newValues,
            });
          }
        }}
        error={!!inputConfig.validate?.(criterion.values?.[0] || '')}
        helperText={inputConfig.helperText}
      />
      <TextField
        label="Max"
        type={fieldType === 'Float' || fieldType === 'Int' ? 'number' : 'text'}
        value={criterion.values?.[1] || ''}
        onChange={(e) => {
          const error = inputConfig.validate?.(e.target.value);
          if (!error) {
            const newValues = [...(criterion.values || [])];
            newValues[1] = e.target.value;
            onChange({
              ...criterion,
              values: newValues,
            });
          }
        }}
        error={!!inputConfig.validate?.(criterion.values?.[1] || '')}
        helperText={inputConfig.helperText}
      />
    </Box>
  );
};
